import { CanActivateFn, Routes } from '@angular/router';

import { PlaceholderPageComponent } from
  '../component/placeholder-page/placeholder-page.component';
import { environment } from '../environments/environment';
import { authGuard } from './shared/guards/auth.guard';

/**
 * Auth policy for every signed-in page, declared ONCE and referenced by each protected
 * route below — change what it takes to enter the app here, not in nine places.
 *
 * Deliberately a shared constant rather than a path-less `canActivateChild` parent: the
 * protected entries are the top-level `loadChildren` feature roots, and nesting them under
 * a componentless parent changes how the router falls through to the `**` wildcard when no
 * child matches. The guard list is the thing that repeats; the route tree is not.
 */
const PROTECTED_ROUTE_GUARDS: CanActivateFn[] = [authGuard];

export const routes: Routes = [
  // Default landing: the standard email/password sign-in flow locally, dashboard
  // in production. The dev token-login flow stays reachable at /token-login but
  // is no longer the root entry point.
  { path: '', redirectTo: () => (environment.production ? 'dashboard' : 'emailVerification'), pathMatch: 'full' },

  // Auth redirect landing (external SSO/redirect entry point)
  {
    path: 'auth-redirect',
    loadComponent: () =>
      import('./auth-redirect/auth-redirect.component').then(
        m => m.AuthRedirectComponent
      )
  },

  // Legacy dev token-entry path. The token-entry page has been superseded by the
  // standard email/password sign-in flow (emailVerification → login →
  // securityQuestions), so this path no longer renders its own page — it forwards
  // to the first step of that flow. Kept (not removed) so the existing bounce
  // targets that navigate here via TOKEN_LOGIN_PATH — SessionExpiryService, the
  // auth guard, and dev logout — plus any stale bookmarks land on the
  // new flow instead of 404ing.
  {
    path: 'token-login',
    redirectTo: 'emailVerification',
    pathMatch: 'full'
  },

  /**
   * ─── Standard email/password sign-in flow ───────────────────────────────
   * Coexists with the dev token-login flow above. Three chromeless steps that
   * mirror the legacy RegTrack login routes (camelCase paths kept for parity):
   *
   * 1. `/emailVerification` — verify-email → decode/store the HMAC verifier.
   * 2. `/login` — password → token-generate, then the user-services chain FIRST.
   * 3. `/securityQuestions` — verify-security-qa → sets `isVerified`, then routes
   *    on to the post-login `/dashboard`.
   *
   * All three set `data: { chromeless: true }` so they render outside the app
   * shell (no sidebar / top bar / breadcrumb). See `AUTH_ROUTE_PATHS` for the
   * navigation constants used between these steps.
   */
  {
    path: 'emailVerification',
    data: { chromeless: true },
    loadComponent: () =>
      import('./authentication/email-verification/email-verification.component').then(
        m => m.EmailVerificationComponent
      )
  },
  {
    path: 'login',
    data: { chromeless: true },
    loadComponent: () =>
      import('./authentication/login/login.component').then(
        m => m.LoginComponent
      )
  },
  {
    path: 'securityQuestions',
    data: { chromeless: true },
    loadComponent: () =>
      import('./authentication/security-questions/security-questions.component').then(
        m => m.SecurityQuestionsComponent
      )
  },

  // Authenticated account action reached from the header profile menu. Chromeless
  // (full-screen, no app shell) like the sign-in pages; authGuard-protected.
  {
    path: 'changePassword',
    canActivate: PROTECTED_ROUTE_GUARDS,
    data: { chromeless: true },
    loadComponent: () =>
      import('./authentication/change-password/change-password.component').then(
        m => m.ChangePasswordComponent
      )
  },

  // Lazy Loaded Dashboard Routes
  {
    path: 'dashboard',
    canActivate: PROTECTED_ROUTE_GUARDS,
    loadChildren: () =>
      import('./routing-modules/dashboard/dashboard.routes').then(
        m => m.DASHBOARD_ROUTES
      )
  },

  // Lazy Loaded License Routes
  {
    path: 'workspace',
    canActivate: PROTECTED_ROUTE_GUARDS,
    loadChildren: () =>
      import('./routing-modules/workspace/workspace.routes').then(
        m => m.WORKSPACE_ROUTES
      )
  },

  // Lazy Loaded Not Activated Licenses drill-down (opened from the dashboard "Not Activated" card)
  {
    path: 'not-activated',
    canActivate: PROTECTED_ROUTE_GUARDS,
    loadChildren: () =>
      import('./routing-modules/not-activated/not-activated.routes').then(
        m => m.NOT_ACTIVATED_ROUTES
      )
  },

  // Lazy Loaded Documents Routes
  {
    path: 'documents',
    canActivate: PROTECTED_ROUTE_GUARDS,
    // Feature-level breadcrumb: every /documents/* page accumulates "Documents"
    // as its parent crumb (children declare only their own label).
    data: { breadcrumb: 'breadcrumbs.documents' },
    loadChildren: () =>
      import('./routing-modules/documents/documents.routes').then(
        m => m.DOCUMENTS_ROUTES
      )
  },

  {
    path: 'home',
    canActivate: PROTECTED_ROUTE_GUARDS,
    component: PlaceholderPageComponent,
    data: {
      label: 'breadcrumbs.home',
      sidebarKey: 'home'
    }
  },

  // Lazy Loaded Reports Routes
  {
    path: 'reports',
    canActivate: PROTECTED_ROUTE_GUARDS,
    loadChildren: () =>
      import('./routing-modules/reports/reports.routes').then(
        m => m.REPORTS_ROUTES
      )
  },

  // Lazy Loaded Masters Routes
  {
    path: 'masters',
    canActivate: PROTECTED_ROUTE_GUARDS,
    // Feature-level breadcrumb: every /masters/* page accumulates "Masters"
    // ahead of its own crumb (e.g. "Masters › License Registration").
    data: { breadcrumb: 'breadcrumbs.masters' },
    loadChildren: () =>
      import('./routing-modules/masters/masters.routes').then(
        m => m.MASTER_ROUTES
      )
  },

  // Lazy Loaded Alerts and Action Routes
  {
    path: 'alerts',
    canActivate: PROTECTED_ROUTE_GUARDS,
    loadChildren: () =>
      import('./routing-modules/alerts-action/alerts-action.routes').then(
        m => m.ALERTS_ACTION_ROUTES
      )
  },

  // ─── Global header search ────────────────────────────────────────────────
  // Reached from the app-shell header's search box (Enter / search icon), which
  // sets the term in SearchBarStore (NOT a query param) and navigates here. The
  // two detail routes are the View-action destinations from the results grid;
  // they are placeholder pages until the real compliance/user detail screens are
  // ported. MUST stay ABOVE the `**` wildcard — a route after the catch-all is
  // unreachable (the wildcard would redirect /searchBar to /dashboard).
  {
    path: 'searchBar',
    canActivate: PROTECTED_ROUTE_GUARDS,
    // `sidebarKey` is non-matching on purpose: search is not a sidebar feature,
    // so no item should highlight. Omitting it defaults to 'dashboard' in
    // AppComponent, which wrongly marks Dashboard active on the search pages.
    data: { breadcrumb: 'breadcrumbs.search', sidebarKey: 'search' },
    loadComponent: () =>
      import('../component/search-bar/search-bar.component').then(
        m => m.SearchBarComponent
      )
  },
  {
    path: 'searchComplianceDetails',
    canActivate: PROTECTED_ROUTE_GUARDS,
    data: { breadcrumb: 'breadcrumbs.complianceDetails', sidebarKey: 'search' },
    loadComponent: () =>
      import('../component/search-bar/search-compliance-details/search-compliance-details.component').then(
        m => m.SearchComplianceDetailsComponent
      )
  },
  {
    path: 'searchBarDetails',
    canActivate: PROTECTED_ROUTE_GUARDS,
    data: { breadcrumb: 'breadcrumbs.searchDetails', sidebarKey: 'search' },
    loadComponent: () =>
      import('../component/search-bar/search-bar-details/search-bar-details.component').then(
        m => m.SearchBarDetailsComponent
      )
  },

  // Lazy Loaded Notifications Routes (top-bar bell destination)
  {
    path: 'notifications',
    canActivate: PROTECTED_ROUTE_GUARDS,
    loadChildren: () =>
      import('./routing-modules/notifications/notifications.routes').then(
        m => m.NOTIFICATIONS_ROUTES
      )
  },
  
  // ─── Top-bar quick-action destinations ──────────────────────────────────
  // Reached from the header's quick-action icons (see
  // `AppComponent.onTopBarAction`), not from the sidebar. Every page here
  // lazy-loads via `loadComponent` (no routing-modules file):
  //   • `message-center` is a componentless PARENT with two children — the list
  //     (index, empty path) and the `view` detail page it opens.
  //   • `announcements` is a single standalone page.
  // Neither maps to a sidebar item, so their `sidebarKey` intentionally matches
  // no entry and the sidebar highlights nothing.
  {
    path: 'message-center',
    canActivate: PROTECTED_ROUTE_GUARDS,
    // Parent (componentless) carries the "Message Center" crumb + sidebar key; the
    // list index inherits them (empty path), the pathed `view` child re-declares
    // sidebarKey (pathed children don't inherit parent data) and adds its own crumb.
    data: { sidebarKey: 'message-center', breadcrumb: 'breadcrumbs.messageCenter' },
    children: [
      {
        path: '',
        data: { label: 'breadcrumbs.messageCenter' },
        loadComponent: () =>
          import('../component/message-center/message-center.component').then(
            m => m.MessageCenterComponent
          )
      },
      {
        path: 'view',
        data: { label: 'breadcrumbs.viewMessageCenter', breadcrumb: 'breadcrumbs.viewMessageCenter', sidebarKey: 'message-center' },
        loadComponent: () =>
          import('../component/message-center/message-view/message-view.component').then(
            m => m.MessageViewComponent
          )
      },
    ],
  },
  {
    path: 'announcements',
    canActivate: PROTECTED_ROUTE_GUARDS,
    data: { label: 'breadcrumbs.whatsNew', sidebarKey: 'announcements', breadcrumb: 'breadcrumbs.whatsNew' },
    loadComponent: () =>
      import('../component/announcements/announcements.component').then(
        m => m.AnnouncementsComponent
      )
  },

  // Catch-all — keep LAST so real routes above are reachable.
  { path: '**', redirectTo: 'dashboard' },
];

